import { useState } from 'react'

import './_accountSettings.scss'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import InputCard from 'components/Form/InputCard/InputCard'
import FixedBottomButton from 'components/UI/FixedBottomButton/FixedBottomButton'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { SuccessfulChange } from 'pages/settings/features/SuccessfulChange'
import { setPassword } from 'services/endpoints/auth'
import { editPassword } from 'services/endpoints/settings/account/profileSettings'
import { useUserProfile } from 'store'

export const ChangePassword = () => {
    const userData = useUserProfile()!
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const hasPassword =
        userData.has_password === true ||
        userData.has_password === 1
    const [currentPage, setCurrentPage] = useState<number>(1)

    const [passState, setPassState] = useState<{
        current: string
        new: string
        confirm: string
    }>({
        current: '',
        new: '',
        confirm: ''
    })

    const [errorMsg, setErrorMsg] = useState<string>('')
    const [submitting, setSubmitting] = useState(false)

    const validateNewPassword = (password: string): boolean => {
        return (
            password.length >= 8 &&
            /[a-z]/.test(password) &&
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password)
        )
    }

    const updatePassword = async () => {
        if (submitting) return

        setErrorMsg('')

        /*
         * Existing passwords should not be checked against the
         * NEW password policy.
         *
         * They only need to be supplied so the backend can run
         * Hash::check().
         */
        if (
            hasPassword &&
            passState.current.trim() === ''
        ) {
            setErrorMsg(
                t('error:incorrectCurrentPassword')
            )
            return
        }

        if (!validateNewPassword(passState.new)) {
            setErrorMsg(
                'The new password must contain at least 8 characters, including uppercase and lowercase letters, and a number.'
            )
            return
        }

        if (passState.new !== passState.confirm) {
            setErrorMsg(
                t('error:passwordsDoNotMatch')
            )
            return
        }

        if (
            hasPassword &&
            passState.current === passState.new
        ) {
            setErrorMsg(
                t(
                    'error:newPasswordCannotBeTheSameAsTheCurrentOne'
                )
            )
            return
        }

        setSubmitting(true)

        try {
            if (!hasPassword) {
                const resp = await setPassword({
                    password: passState.new,
                    password_confirmation: passState.confirm
                })

                if (
                    resp?.message &&
                    !resp.message.includes(
                        'Password has been set successfully'
                    )
                ) {
                    setErrorMsg(
                        t(
                            'error:anErrorOccuredPleaseTryAgain'
                        )
                    )
                    return
                }
            } else {
                await editPassword({
                    old_password: passState.current,
                    password: passState.new,
                    password_confirmation:
                        passState.confirm
                })
            }

            await queryClient.invalidateQueries(
                'user-profile'
            )

            setPassState({
                current: '',
                new: '',
                confirm: ''
            })

            setCurrentPage(2)
        } catch (error: any) {
            const responseData =
                error?.response?.data

            /*
             * Your backend validation responses have historically
             * used { message: { field: [...] } }.
             *
             * Also support Laravel's conventional `errors` shape.
             */
            const errors =
                responseData?.errors ??
                (
                    typeof responseData?.message ===
                    'object'
                        ? responseData.message
                        : null
                )

            if (errors?.old_password?.length) {
                setErrorMsg(
                    t(
                        'error:incorrectCurrentPassword'
                    )
                )
                return
            }

            if (errors?.password?.length) {
                setErrorMsg(
                    String(errors.password[0])
                )
                return
            }

            if (
                typeof responseData?.message ===
                'string' &&
                responseData.message
            ) {
                setErrorMsg(
                    responseData.message
                )
                return
            }

            setErrorMsg(
                t(
                    'error:anErrorOccuredPleaseTryAgain'
                )
            )
        } finally {
            setSubmitting(false)
        }
    }

    const renderCurrentPage = () => {
        switch (currentPage) {
            case 1:
                return (
                    <>
                        {hasPassword && (
                            <InputCard
                                type='password'
                                label={t(
                                    'currentPassword'
                                )}
                                value={
                                    passState.current
                                }
                                changeFn={(
                                    val: string
                                ) => {
                                    setErrorMsg('')

                                    setPassState(
                                        prev => ({
                                            ...prev,
                                            current: val
                                        })
                                    )
                                }}
                                hasTypeChanger={true}
                            />
                        )}

                        <InputCard
                            type='password'
                            label={t('newPassword')}
                            value={passState.new}
                            changeFn={(val: string) => {
                                setErrorMsg('')

                                setPassState(prev => ({
                                    ...prev,
                                    new: val
                                }))
                            }}
                            hasTypeChanger={true}
                            validate='password'
                        />

                        <InputCard
                            type='password'
                            label={t(
                                'confirmNewPassword'
                            )}
                            value={passState.confirm}
                            changeFn={(val: string) => {
                                setErrorMsg('')

                                setPassState(prev => ({
                                    ...prev,
                                    confirm: val
                                }))
                            }}
                            hasTypeChanger={true}
                            validate='password'
                        />

                        <div className='password-requirements'>
                            The password must not be
                            the same as the current one
                            and must contain at least 8
                            characters, including
                            uppercase and lowercase
                            letters, and a number.
                        </div>
                    </>
                )

            case 2:
                return (
                    <SuccessfulChange
                        img={Icons.password_big}
                        text={t(
                            'yourPasswordHasSuccessfullyChanged'
                        )}
                    />
                )

            default:
                return null
        }
    }

    const renderBottomButton = () => {
        switch (currentPage) {
            case 1: {
                const currentPasswordValid =
                    !hasPassword ||
                    passState.current.trim() !== ''

                const newPasswordValid =
                    validateNewPassword(
                        passState.new
                    )

                const confirmationValid =
                    passState.new ===
                        passState.confirm &&
                    passState.confirm.length > 0

                const differentFromCurrent =
                    !hasPassword ||
                    passState.current !== passState.new

                const disabled =
                    submitting ||
                    !currentPasswordValid ||
                    !newPasswordValid ||
                    !confirmationValid ||
                    !differentFromCurrent

                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={
                            !hasPassword
                                ? t('Set Password')
                                : t('update')
                        }
                        disabled={disabled}
                        clickFn={() => {
                            void updatePassword()
                        }}
                    />
                )
            }

            case 2:
                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={t('close')}
                        clickFn={() => {
                            navigate(
                                '/settings/account'
                            )
                        }}
                    />
                )

            default:
                return null
        }
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('account')}
                    title={
                        !hasPassword
                            ? t('Set Password')
                            : t('settings:changePassword')
                    }
                />
            }
        >
            <>
                {renderCurrentPage()}
                {renderBottomButton()}

                {errorMsg ? (
                    <div className='accountsettings__card__error'>
                        <p>{errorMsg}</p>
                    </div>
                ) : null}
            </>
        </WithHeaderSection>
    )
}